import { LASSO } from './map-style';
import { Rgb } from './map-types';

/**
 * The lasso tools on the suburb map (#256): freehand (hold and draw, release applies) and polygonal (click corners;
 * double-click, Enter or clicking the first corner closes and applies; Esc cancels), drawn on a canvas over the map.
 * Cmd/Ctrl held arms a freehand lasso from any tool (ModifierKey below).
 */

/** A click this close (px) to the polygon's first corner closes it. */
const CLOSE_RADIUS = 12;
/** A lasso needs three corners to enclose anything. */
const MIN_CORNERS = 3;

/** What the lasso asks of the engine. */
export interface LassoHost {
  /** Whether the canvas takes pointer events now (an editing mode, and a lasso tool or the modifier held). */
  armed(): boolean;
  /** Freehand: the lasso tool, or the modifier held over any tool but the polygon. */
  freehand(): boolean;
  polygon(): boolean;
  colour(): Rgb;
  apply(path: number[][]): void;
  status(message: string): void;
}

export class LassoTool {
  /** True while a lasso or polygon is being drawn (Esc cancels it, rather than closing anything else). */
  drawing = false;

  constructor(
    private canvas: HTMLCanvasElement,
    private host: LassoHost
  ) {}

  /** Listen on the canvas (and the document, for Enter and Esc); returns the teardown. */
  wire(): () => void {
    const cv = this.canvas;
    let poly: number[][] | null = null;
    let free: number[][] | null = null;
    const xy = (e: MouseEvent) => {
      const r = cv.getBoundingClientRect();
      return [e.clientX - r.left, e.clientY - r.top];
    };
    const finish = (path: number[][] | null) => {
      this.drawing = false;
      this.clear();
      if (path && path.length >= MIN_CORNERS) this.host.apply(path);
    };
    const down = (e: PointerEvent) => {
      if (!this.host.armed()) return;
      e.preventDefault();
      if (this.host.freehand()) {
        free = [xy(e)];
        this.drawing = true;
        cv.setPointerCapture(e.pointerId);
        return;
      }
      const p = xy(e);
      if (
        poly &&
        poly.length >= MIN_CORNERS &&
        Math.hypot(p[0] - poly[0][0], p[1] - poly[0][1]) < CLOSE_RADIUS
      ) {
        finish(poly);
        poly = null;
        return;
      }
      poly = poly ? [...poly, p] : [p];
      this.drawing = true;
      this.draw(poly);
    };
    const move = (e: PointerEvent) => {
      if (free) {
        free.push(xy(e));
        this.draw(free);
      } else if (poly) {
        this.draw([...poly, xy(e)]);
      }
    };
    const up = (e: PointerEvent) => {
      if (!free) return;
      cv.releasePointerCapture(e.pointerId);
      const path = free;
      free = null;
      finish(path);
    };
    const dbl = (e: MouseEvent) => {
      if (!this.host.polygon() || !poly) return;
      e.preventDefault();
      finish(poly);
      poly = null;
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (poly || free)) {
        poly = null;
        free = null;
        this.drawing = false;
        this.clear();
        this.host.status('Lasso cancelled.');
      }
      if (e.key === 'Enter' && poly) {
        finish(poly);
        poly = null;
      }
    };
    cv.addEventListener('pointerdown', down);
    cv.addEventListener('pointermove', move);
    cv.addEventListener('pointerup', up);
    cv.addEventListener('dblclick', dbl);
    document.addEventListener('keydown', key);
    return () => {
      cv.removeEventListener('pointerdown', down);
      cv.removeEventListener('pointermove', move);
      cv.removeEventListener('pointerup', up);
      cv.removeEventListener('dblclick', dbl);
      document.removeEventListener('keydown', key);
    };
  }

  /** Match the canvas's backing store to its CSS size and the screen's pixel ratio. */
  fit(): void {
    const cv = this.canvas;
    const dpr = window.devicePixelRatio || 1;
    const w = Math.round(cv.clientWidth * dpr);
    const h = Math.round(cv.clientHeight * dpr);
    if (cv.width !== w || cv.height !== h) {
      cv.width = w;
      cv.height = h;
    }
  }

  clear(): void {
    this.canvas.getContext('2d')?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /** The path so far, dashed, with a dot on each corner. */
  private draw(path: number[][]): void {
    this.fit();
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, this.canvas.clientWidth, this.canvas.clientHeight);
    if (path.length < 2) return;
    const rgb = this.host.colour();
    const colour = `rgb(${rgb.join(',')})`;
    ctx.beginPath();
    ctx.moveTo(path[0][0], path[0][1]);
    for (const [x, y] of path.slice(1)) ctx.lineTo(x, y);
    ctx.strokeStyle = colour;
    ctx.lineWidth = LASSO.width;
    ctx.setLineDash(LASSO.dash);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = colour;
    for (const [x, y] of path) {
      ctx.beginPath();
      ctx.arc(x, y, LASSO.cornerRadius, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/** Cmd (mac) / Ctrl held on its own arms the lasso; released, back to the tool. */
export class ModifierKey {
  held = false;

  /** Track the key on the window; `changed` runs when it goes down or up. Returns the teardown. */
  wire(changed: () => void): () => void {
    const update = (e: KeyboardEvent) => {
      const bare = e.key === 'Meta' || e.key === 'Control';
      if (e.type === 'keydown' && !bare) return; // Cmd+Z etc. are shortcuts, not a lasso
      const held = (e.metaKey || e.ctrlKey) && !(e.type === 'keyup' && bare);
      if (held === this.held) return;
      this.held = held;
      changed();
    };
    const blur = () => {
      if (!this.held) return;
      this.held = false;
      changed();
    };
    window.addEventListener('keydown', update);
    window.addEventListener('keyup', update);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', update);
      window.removeEventListener('keyup', update);
      window.removeEventListener('blur', blur);
    };
  }
}
